import { z } from 'zod';

// ── LocationHotelConfig schemas (JQ4-17 · US-CONFIG-003) ─────────────────────
//
// Operational config for Hotel-niche locations.
// Controls overnight cutoff hour and vehicle in/out policy.
// Amounts are INTEGER CENTS. UI converts dollars<->cents at the form boundary.

// ── InOutPolicy ───────────────────────────────────────────────────────────────

export const IN_OUT_POLICIES = ['unlimited', 'max_exits', 'per_exit_fee'] as const;

export const InOutPolicySchema = z.enum(IN_OUT_POLICIES);
export type InOutPolicy = z.infer<typeof InOutPolicySchema>;

// ── UpsertHotelConfigRequest ──────────────────────────────────────────────────
//
// Cross-field invariants (REQ-3.2):
//   - inOutPolicy=max_exits  → maxExits must be present (positive integer)
//   - inOutPolicy=per_exit_fee → perExitFeeCents must be present (>= 0)
//   - inOutPolicy=unlimited  → maxExits and perExitFeeCents must be null/absent

export const UpsertHotelConfigRequestSchema = z
  .object({
    // overnightCutoffHour: only 0 (midnight) or 6 (6 am) are allowed (REQ-2.4)
    overnightCutoffHour: z.union([z.literal(0), z.literal(6)]),
    inOutPolicy: InOutPolicySchema,
    maxExits: z.number().int().positive().nullable().optional(),
    perExitFeeCents: z.number().int().nonnegative().nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.inOutPolicy === 'max_exits') {
      if (v.maxExits == null) {
        ctx.addIssue({
          code: 'custom',
          path: ['maxExits'],
          message: 'maxExits is required when inOutPolicy is max_exits',
        });
      }
    }

    if (v.inOutPolicy === 'per_exit_fee') {
      if (v.perExitFeeCents == null) {
        ctx.addIssue({
          code: 'custom',
          path: ['perExitFeeCents'],
          message: 'perExitFeeCents is required when inOutPolicy is per_exit_fee',
        });
      }
    }

    if (v.inOutPolicy === 'unlimited') {
      if (v.maxExits != null) {
        ctx.addIssue({
          code: 'custom',
          path: ['maxExits'],
          message: 'maxExits must be null when inOutPolicy is unlimited',
        });
      }
      if (v.perExitFeeCents != null) {
        ctx.addIssue({
          code: 'custom',
          path: ['perExitFeeCents'],
          message: 'perExitFeeCents must be null when inOutPolicy is unlimited',
        });
      }
    }
  });

export type UpsertHotelConfigRequest = z.infer<typeof UpsertHotelConfigRequestSchema>;

// ── HotelConfigResponse ───────────────────────────────────────────────────────
//
// Returned by GET and PUT /admin/locations/:locationId/hotel-config.
// When no row exists the use case applies defaults (overnightCutoffHour: 6,
// inOutPolicy: 'unlimited') without writing to the DB.

export const HotelConfigResponseSchema = z.object({
  id: z.string().uuid().optional(), // absent when responding with defaults (no row yet)
  locationId: z.string().uuid(),
  organizationId: z.string().uuid().optional(), // absent for default response
  overnightCutoffHour: z.union([z.literal(0), z.literal(6)]),
  inOutPolicy: InOutPolicySchema,
  maxExits: z.number().int().positive().nullable(),
  perExitFeeCents: z.number().int().nonnegative().nullable(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type HotelConfigResponse = z.infer<typeof HotelConfigResponseSchema>;
